'use client';

import type { ColumnsBlock, PageBlock } from '@impact/types';
import { cn } from '@/lib/utils';
import type { EditableWrapper } from './types';
import { BlockRenderer } from './BlockRenderer';
import { AddNestedBlockButton } from './_editing/AddNestedBlockButton';

interface ColumnsBlockRendererProps {
  block: ColumnsBlock;
  editing?: boolean;
  Wrapper?: EditableWrapper;
}

const LAYOUT_CLASSES = {
  '1-1': 'md:grid-cols-2',
  '1-2': 'md:grid-cols-[1fr_2fr]',
  '2-1': 'md:grid-cols-[2fr_1fr]',
  '1-1-1': 'md:grid-cols-3',
  '1-2-1': 'md:grid-cols-[1fr_2fr_1fr]',
} as const;

const GAP_CLASSES = {
  sm: 'gap-4',
  md: 'gap-6',
  lg: 'gap-8',
} as const;

const LAYOUT_COLUMN_COUNT = {
  '1-1': 2,
  '1-2': 2,
  '2-1': 2,
  '1-1-1': 3,
  '1-2-1': 3,
} as const;

export function ColumnsBlockRenderer({ block, editing, Wrapper }: ColumnsBlockRendererProps) {
  const { columns, layout, gap = 'md' } = block.data;

  if (!editing && (!columns || columns.length === 0)) {
    return null;
  }

  const expectedCount = LAYOUT_COLUMN_COUNT[layout];
  const safeColumns = (columns ?? []).slice(0, expectedCount);
  // Pad missing columns when editing so the layout shows the expected slots
  const renderColumns =
    editing && safeColumns.length < expectedCount
      ? [
          ...safeColumns,
          ...Array.from({ length: expectedCount - safeColumns.length }, () => ({
            id: `placeholder-${Math.random()}`,
            blocks: [],
          })),
        ]
      : safeColumns;

  return (
    <div className={cn('my-8 grid grid-cols-1', LAYOUT_CLASSES[layout], GAP_CLASSES[gap])}>
      {renderColumns.map((column, columnIndex) => {
        const childBlocks = (column.blocks ?? []) as PageBlock[];
        const isEmpty = childBlocks.length === 0;

        if (!editing) {
          return (
            <div key={column.id} className="min-w-0">
              {childBlocks.map((child) => (
                <BlockRenderer key={child.id} block={child} editing={editing} Wrapper={Wrapper} />
              ))}
            </div>
          );
        }

        return (
          <div
            key={column.id}
            className={cn(
              'min-w-0 rounded-lg p-2 transition-colors',
              isEmpty
                ? 'border-2 border-dashed border-muted-foreground/20'
                : 'border border-dashed border-transparent hover:border-muted-foreground/20',
            )}
          >
            {childBlocks.map((child) => (
              <BlockRenderer key={child.id} block={child} editing={editing} Wrapper={Wrapper} />
            ))}
            <div
              className={cn('flex w-full', isEmpty ? 'justify-center py-4' : 'justify-center pt-2')}
            >
              <AddNestedBlockButton
                blockId={block.id}
                arrayPath={`data.columns[${columnIndex}].blocks`}
                variant={isEmpty ? 'placeholder' : 'inline'}
                label={isEmpty ? `Add to column ${columnIndex + 1}` : 'Add block'}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
